import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GAME_MODE_DEFS } from './ModeSelector';

const RuleCard = ({ mode, lang, onToggleLang, onHost }) => {
    const rules = lang === 'hi' ? mode.rules_hi : mode.rules_en;
    return (
        <div id={mode.value} style={{
            borderRadius: 16,
            overflow: 'hidden',
            border: `1px solid ${mode.color}44`,
            boxShadow: `0 4px 24px ${mode.color}22`,
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 8px 32px ${mode.color}44`; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = `0 4px 24px ${mode.color}22`; }}
        >
            {/* Header */}
            <div style={{ background: mode.gradient, padding: '18px 20px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ fontSize: 32 }}>{mode.emoji}</span>
                        <div>
                            <div style={{ color: 'white', fontWeight: 800, fontSize: 17 }}>{mode.label}</div>
                            <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 12, marginTop: 2 }}>{mode.subtitle}</div>
                        </div>
                    </div>
                    <button onClick={onToggleLang}
                        style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 20, color: 'white', padding: '4px 12px', fontSize: 12, cursor: 'pointer', backdropFilter: 'blur(4px)' }}
                    >{lang === 'en' ? '🇮🇳 हिन्दी' : '🇬🇧 English'}</button>
                </div>

                {/* Min players badge */}
                {mode.minPlayers > 2 && (
                    <div style={{ marginTop: 8, display: 'inline-block', background: 'rgba(255,255,255,0.15)', borderRadius: 20, padding: '3px 10px', fontSize: 11, color: 'white' }}>
                        👥 Requires {mode.minPlayers} players
                    </div>
                )}
            </div>

            {/* Rules */}
            <div style={{ padding: '16px 20px', background: 'rgba(255,255,255,0.03)' }}>
                <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                    {rules.map((rule, i) => (
                        <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '7px 0', borderBottom: i < rules.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
                            <span style={{ minWidth: 22, height: 22, borderRadius: '50%', background: mode.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: 'white', flexShrink: 0, marginTop: 1 }}>{i + 1}</span>
                            <span style={{ color: lang === 'hi' ? 'rgba(255,220,100,0.9)' : 'rgba(255,255,255,0.88)', fontSize: 13.5, lineHeight: 1.55 }}>{rule}</span>
                        </li>
                    ))}
                </ul>

                {/* Host This Mode CTA */}
                <button onClick={() => onHost(mode.value)}
                    style={{
                        marginTop: 14,
                        width: '100%',
                        padding: '11px 0',
                        borderRadius: 10,
                        border: `1px solid ${mode.color}66`,
                        background: `${mode.color}22`,
                        color: 'white',
                        fontWeight: 700,
                        fontSize: 14,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        letterSpacing: 0.3,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = `${mode.color}44`; e.currentTarget.style.boxShadow = `0 4px 14px ${mode.color}44`; }}
                    onMouseLeave={e => { e.currentTarget.style.background = `${mode.color}22`; e.currentTarget.style.boxShadow = 'none'; }}
                >
                    🎮 Host a Room in {mode.emoji} {mode.label}
                </button>
            </div>
        </div>
    );
};

const GameModesPage = () => {
    const navigate = useNavigate();
    const [cardLangs, setCardLangs] = useState(() => Object.fromEntries(GAME_MODE_DEFS.map(m => [m.value, 'en'])));
    const [globalLang, setGlobalLang] = useState('en');

    const toggleCardLang = id => setCardLangs(prev => ({ ...prev, [id]: prev[id] === 'en' ? 'hi' : 'en' }));

    const toggleAll = () => {
        const next = globalLang === 'en' ? 'hi' : 'en';
        setGlobalLang(next);
        setCardLangs(Object.fromEntries(GAME_MODE_DEFS.map(m => [m.value, next])));
    };

    const handleHost = modeValue => {
        // Navigate to home and open host modal with pre-selected mode
        navigate('/', { state: { openHost: true, mode: modeValue } });
    };

    return (
        <div style={{ minHeight: '100vh', paddingTop: 64, paddingLeft: 0, paddingBottom: 100 }} className="md:pl-16">
            {/* Top bar */}
            <div style={{ position: 'sticky', top: 0, zIndex: 50, background: 'rgba(15,12,41,0.85)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.08)', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <button onClick={() => navigate(-1)} style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 10, color: 'white', padding: '6px 14px', fontSize: 13, cursor: 'pointer' }}>← Back</button>
                    <div>
                        <div style={{ color: 'white', fontWeight: 800, fontSize: 18 }}>🎲 Game Modes</div>
                        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>खेल के नियम — Rules & How to Play</div>
                    </div>
                </div>
                <button onClick={toggleAll}
                    style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', border: 'none', borderRadius: 20, color: 'white', padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(99,102,241,0.4)' }}
                >{globalLang === 'en' ? '🇮🇳 सभी हिन्दी में' : '🇬🇧 All in English'}</button>
            </div>

            {/* Hero */}
            <div style={{ textAlign: 'center', padding: '36px 20px 24px' }}>
                <div style={{ fontSize: 48, marginBottom: 8 }}>🎲</div>
                <h1 style={{ color: 'white', fontSize: 28, fontWeight: 900, margin: 0 }}>Ludo Game Modes</h1>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14, marginTop: 8 }}>5 unique modes — Classic, Reverse, Token, No-Cut, and 2v2 Team</p>

                {/* Quick pills */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginTop: 16 }}>
                    {GAME_MODE_DEFS.map(m => (
                        <a key={m.value} href={`#${m.value}`}
                            style={{ background: `${m.color}22`, border: `1px solid ${m.color}66`, borderRadius: 20, color: 'white', padding: '5px 14px', fontSize: 12, textDecoration: 'none' }}
                        >{m.emoji} {m.label}</a>
                    ))}
                </div>
            </div>

            {/* Cards */}
            <div style={{ maxWidth: 760, margin: '0 auto', padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 20 }}>
                {GAME_MODE_DEFS.map(mode => (
                    <RuleCard
                        key={mode.value}
                        mode={mode}
                        lang={cardLangs[mode.value]}
                        onToggleLang={() => toggleCardLang(mode.value)}
                        onHost={handleHost}
                    />
                ))}
            </div>

            {/* Bottom CTA */}
            <div style={{ textAlign: 'center', marginTop: 40, padding: '0 20px' }}>
                <button onClick={() => navigate('/')}
                    style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', border: 'none', borderRadius: 14, color: 'white', padding: '14px 36px', fontSize: 15, fontWeight: 700, cursor: 'pointer', boxShadow: '0 6px 20px rgba(99,102,241,0.5)', letterSpacing: 0.5 }}
                >🎮 Go to Lobby</button>
            </div>
        </div>
    );
};

export default GameModesPage;
